/**
 * /analytics — marketing analytics (Meta Pixel · Microsoft Clarity · GA4 ·
 * Instagram).
 *
 * Sourced from `GET /api/analytics`: the latest snapshot per source from
 * `website_analytics_snapshots` (written daily by the
 * `website_analytics_daily` cron saga) plus the cross-row Clarity trend.
 * Meta + GA4 trends come from the latest snapshot's own 28-day daily
 * series; Clarity's API window is only 1..3 days, so its trend is
 * assembled across the stored days. A source whose last pull failed
 * renders dark with its stable error code. Read-only, staff-facing.
 *
 * 2026-08-06 (migration 155) — the Instagram section reads
 * `instagram_insights_snapshots`, written by the SEPARATE
 * `instagram_insights_daily` cron. Separate table and cron because the two
 * gate on different config (an L2 `website_analytics` block vs the Instagram
 * connector + its secrets); the PAGE is where they unify, which is where it
 * matters to a human.
 */

import { useEffect, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  api,
  ApiError,
  type WebsiteAnalyticsLatestSnapshot,
  type WebsiteAnalyticsResponse,
} from "../lib/api";

type Rec = Record<string, unknown>;

const rec = (v: unknown): Rec =>
  typeof v === "object" && v !== null && !Array.isArray(v) ? (v as Rec) : {};
const arr = (v: unknown): ReadonlyArray<unknown> => (Array.isArray(v) ? v : []);
const num = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) ? v : null;
const str = (v: unknown): string | null => (typeof v === "string" ? v : null);

const fmtInt = (v: number | null): string =>
  v === null ? "—" : v.toLocaleString("en-GB");

/** `2026-07-24` → `24 Jul` (chart tick / table label). */
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

/** Relative freshness of the pixel's last_fired_time (Meta's alive signal). */
function relativeTime(isoish: string): string {
  const d = new Date(isoish);
  if (Number.isNaN(d.getTime())) return isoish;
  const mins = Math.round((Date.now() - d.getTime()) / 60_000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h ago`;
  return `${Math.round(mins / (24 * 60))}d ago`;
}

/** A failed pull stores `{error: <stable code>}` as the day's payload. */
function snapshotError(
  snap: WebsiteAnalyticsLatestSnapshot | null,
): string | null {
  return snap === null ? null : str(snap.payload["error"]);
}

function SourceState(props: {
  readonly snap: WebsiteAnalyticsLatestSnapshot | null;
  readonly name: string;
}): JSX.Element | null {
  const err = snapshotError(props.snap);
  if (props.snap === null) {
    return (
      <p className="empty">
        No {props.name} snapshot yet — the <code>website_analytics_daily</code>{" "}
        cron runs at 05:45 UTC (or invoke it from Inngest / the Status page).
      </p>
    );
  }
  if (err !== null) {
    return (
      <p className="member-note">
        <span className="tag tag-danger">{err}</span>{" "}
        <span className="subtle">
          last pull failed on {props.snap.captured_on} — check the secret /
          vendor and re-invoke the cron.
        </span>
      </p>
    );
  }
  return null;
}

const CHART_HEIGHT = 220;
const AXIS_STYLE = { fontSize: 11 } as const;

export function Analytics(): JSX.Element {
  const [data, setData] = useState<WebsiteAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .analytics()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setError(
            e instanceof ApiError
              ? e.code ?? `http_${e.status}`
              : (e as Error).message,
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error !== null)
    return <p className="empty">Couldn&apos;t load analytics ({error}).</p>;
  if (data === null) return <p className="empty">Loading analytics…</p>;

  const ga4 = data.latest.ga4;
  const meta = data.latest.meta_pixel;
  const clarity = data.latest.clarity;
  const funnel = data.checkout_funnel;
  const ga4Ok = ga4 !== null && snapshotError(ga4) === null;
  const metaOk = meta !== null && snapshotError(meta) === null;
  const clarityOk = clarity !== null && snapshotError(clarity) === null;

  // --- GA4 views -----------------------------------------------------------
  const ga4Totals = rec(ga4?.payload["totals"]);
  const ga4Daily = arr(ga4?.payload["daily"]).map(rec);
  const ga4Channels = arr(ga4?.payload["channels"]).map(rec);
  const ga4Pages = arr(ga4?.payload["pages"]).map(rec);
  const ga4Events = arr(ga4?.payload["events"]).map(rec);
  const ga4Hostnames = arr(ga4?.payload["hostnames"]).map(String);
  const ga4Chart = ga4Daily.map((d) => ({
    date: shortDate(str(d["date"]) ?? ""),
    sessions: num(d["sessions"]) ?? 0,
    users: num(d["users"]) ?? 0,
  }));

  // --- Meta Pixel views ----------------------------------------------------
  const metaEvents = arr(meta?.payload["events"]).map(rec);
  const metaDaily = arr(meta?.payload["daily"]).map(rec);
  const metaChart = metaDaily.map((d) => {
    const events = rec(d["events"]);
    let total = 0;
    for (const v of Object.values(events)) total += num(v) ?? 0;
    return { date: shortDate(str(d["date"]) ?? ""), events: total };
  });
  const metaLastFired = str(meta?.payload["last_fired_time"]);
  // Non-null when the token could read the pixel's identity but not its
  // `/stats` edge — counts are UNAVAILABLE (not zero) until the token gains
  // stats access on the pixel's Business.
  const metaStatsError = str(meta?.payload["stats_error"]);

  // --- Clarity views -------------------------------------------------------
  const claritySummary = rec(clarity?.payload["summary"]);
  const clarityPages = arr(clarity?.payload["pages"]).map(rec);
  const clarityDevices = arr(clarity?.payload["devices"]).map(rec);
  const claritySources = arr(clarity?.payload["sources"]).map(rec);
  const clarityChart = data.clarity_trend.map((p) => ({
    date: shortDate(p.captured_on),
    sessions: p.sessions ?? 0,
    rage: p.rage_clicks ?? 0,
  }));
  const frictionClicks =
    (num(claritySummary["rage_clicks"]) ?? 0) +
    (num(claritySummary["dead_clicks"]) ?? 0);
  const scrollDepth = num(claritySummary["avg_scroll_depth"]);

  // --- Instagram views (migration 155) -------------------------------------
  const ig = data.instagram;
  const igError = ig === null ? null : str(ig.payload["error"]);
  const igAccount = rec(ig?.payload["account"]);
  const igDay = rec(ig?.payload["day"]);
  const igUsername = str(igAccount["username"]);
  const igFollowers = num(igAccount["followers_count"]);
  const igReachBreakdown =
    ig === null || ig.payload["reach_breakdown"] === null
      ? null
      : rec(ig.payload["reach_breakdown"]);
  const igTopMedia = arr(ig?.payload["top_media"]).map(rec);
  // 2026-08-06 (migration 156) — stories come off their OWN response field,
  // not out of the snapshot payload, because they are keyed by story and
  // outlive any single day's snapshot.
  const igStories = data.instagram_stories ?? [];
  // Meta's own bucket label — the honest answer to "which day is this?", since
  // captured_on is the day the CRON ran and IG's buckets close at 07:00Z.
  const igDayEnd = str(ig?.payload["day_end_time"]);
  const igDayLabel = igDayEnd === null ? null : shortDate(igDayEnd.slice(0, 10));
  // Both charts come from the snapshot's OWN 30-day series, not from the
  // cross-row trend — one snapshot already carries a month, so the charts are
  // full on day one rather than growing a point per day.
  const igSeries = rec(ig?.payload["series"]);
  const igReachChart = arr(igSeries["reach"]).map(rec).map((p) => ({
    date: shortDate(str(p["end_time"])?.slice(0, 10) ?? ""),
    reach: num(p["value"]) ?? 0,
  }));
  const igFollowerChart = arr(igSeries["follower_count"]).map(rec).map((p) => ({
    date: shortDate(str(p["end_time"])?.slice(0, 10) ?? ""),
    followers: num(p["value"]) ?? 0,
  }));

  return (
    <div>
      <h1 className="page-title">Analytics</h1>
      <p className="member-note">
        Website analytics for{" "}
        <strong>{ga4Hostnames.join(", ") || "the public site"}</strong> — Meta
        Pixel, Microsoft Clarity, and GA4, snapshotted daily by the{" "}
        <code>website_analytics_daily</code> cron.
        {ga4 !== null || meta !== null || clarity !== null ? (
          <span className="subtle">
            {" "}
            · latest snapshot{" "}
            {[ga4, meta, clarity]
              .filter((s): s is WebsiteAnalyticsLatestSnapshot => s !== null)
              .map((s) => s.captured_on)
              .sort()
              .at(-1)}
          </span>
        ) : null}
      </p>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="label">Sessions · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["sessions"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Users · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["users"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Key events · 28d (GA4)</div>
          <div className="value">
            {ga4Ok ? fmtInt(num(ga4Totals["key_events"])) : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Pixel events · 28d</div>
          <div className="value">
            {metaOk && metaStatsError === null
              ? fmtInt(num(meta.payload["total_events"]))
              : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Pixel last fired</div>
          <div className="value">
            {metaOk && metaLastFired !== null
              ? relativeTime(metaLastFired)
              : "—"}
          </div>
        </div>
        <div className="stat-card">
          <div className="label">Friction clicks · 3d (Clarity)</div>
          <div className="value">{clarityOk ? fmtInt(frictionClicks) : "—"}</div>
        </div>
      </div>

      <section className="panel">
        <h2>Checkout funnel — last {funnel.window_days} days</h2>
        <p className="member-note">
          Layer&apos;s own <code>purchase_attempts</code> substrate — every
          PaymentIntent / Checkout Session the website (or a chat channel)
          minted, and where it ended up. Swept every 15 minutes; alerts + recovery
          emails ride the same rows.
        </p>
        {funnel.started === 0 &&
        funnel.succeeded === 0 &&
        funnel.failed === 0 &&
        funnel.abandoned === 0 ? (
          <p className="empty">
            No purchase attempts captured in the window yet — attempts appear
            once the Stripe webhook subscribes to the{" "}
            <code>payment_intent.*</code> / <code>checkout.session.*</code>{" "}
            events.
          </p>
        ) : (
          <>
            <div className="stat-grid">
              <div className="stat-card">
                <div className="label">Started</div>
                <div className="value">{fmtInt(funnel.started)}</div>
              </div>
              <div className="stat-card">
                <div className="label">Paid</div>
                <div className="value">
                  {fmtInt(funnel.succeeded)}
                  {funnel.started > 0 ? (
                    <span className="subtle">
                      {" "}
                      ({Math.round((funnel.succeeded / funnel.started) * 100)}%)
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Failed</div>
                <div className="value">{fmtInt(funnel.failed)}</div>
              </div>
              <div className="stat-card">
                <div className="label">Abandoned</div>
                <div className="value">{fmtInt(funnel.abandoned)}</div>
              </div>
              <div className="stat-card">
                <div className="label">£ lost</div>
                <div className="value">
                  £{(funnel.lost_amount_minor / 100).toFixed(2)}
                </div>
              </div>
            </div>
            {funnel.top_lost_packages.length > 0 ? (
              <div className="analytics-tables">
                <div>
                  <h3>Most abandoned packages</h3>
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Package</th>
                        <th>Lost attempts</th>
                      </tr>
                    </thead>
                    <tbody>
                      {funnel.top_lost_packages.map((pkg) => (
                        <tr key={pkg.package_slug}>
                          <td>{pkg.package_slug}</td>
                          <td>{fmtInt(pkg.count)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : null}
          </>
        )}
      </section>

      <section className="panel">
        <h2>Traffic — GA4</h2>
        <SourceState snap={ga4} name="GA4" />
        {ga4Ok && ga4Chart.length === 0 ? (
          <p className="member-note subtle">
            No sessions recorded for {ga4Hostnames.join(", ")} in the last{" "}
            {num(ga4.payload["window_days"]) ?? 28} days yet — GA4 processes
            new-site data with a lag, and this property is scoped to those
            hostnames only. Numbers fill in as traffic accrues.
          </p>
        ) : null}
        {ga4Ok && ga4Chart.length > 0 ? (
          <>
            <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
              <LineChart data={ga4Chart} margin={{ top: 8, right: 16 }}>
                <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                <XAxis dataKey="date" tick={AXIS_STYLE} minTickGap={24} />
                <YAxis tick={AXIS_STYLE} allowDecimals={false} width={36} />
                <Tooltip />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="sessions"
                  stroke="#1B33C0"
                  strokeWidth={2}
                  dot={false}
                />
                <Line
                  isAnimationActive={false}
                  type="monotone"
                  dataKey="users"
                  stroke="#22975a"
                  strokeWidth={1.5}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
            <div className="analytics-tables">
              <div>
                <h3>Channels</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Channel</th>
                      <th>Sessions</th>
                      <th>Users</th>
                      <th>Key events</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Channels.map((c, i) => (
                      <tr key={i}>
                        <td>{str(c["channel"]) ?? "—"}</td>
                        <td>{fmtInt(num(c["sessions"]))}</td>
                        <td>{fmtInt(num(c["users"]))}</td>
                        <td>{fmtInt(num(c["key_events"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Top pages</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Path</th>
                      <th>Views</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Pages.slice(0, 10).map((p, i) => (
                      <tr key={i}>
                        <td>{str(p["path"]) ?? "—"}</td>
                        <td>{fmtInt(num(p["views"]))}</td>
                        <td>{fmtInt(num(p["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Events</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Event</th>
                      <th>Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ga4Events.slice(0, 12).map((e, i) => (
                      <tr key={i}>
                        <td>{str(e["event"]) ?? "—"}</td>
                        <td>{fmtInt(num(e["count"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </section>

      <section className="panel">
        <h2>Conversion events — Meta Pixel</h2>
        <SourceState snap={meta} name="Meta Pixel" />
        {metaOk && metaStatsError !== null ? (
          <p className="member-note">
            <span className="tag tag-danger">{metaStatsError}</span>{" "}
            <span className="subtle">
              the pixel is alive (identity + last-fired readable) but the
              token can&apos;t read event counts — it needs stats access on
              the pixel&apos;s Business (regenerate with ads_read and update
              the meta.ads_access_token secret).
            </span>
          </p>
        ) : null}
        {metaOk && metaStatsError === null ? (
          <>
            {metaChart.length > 0 ? (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <BarChart data={metaChart} margin={{ top: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                  <XAxis dataKey="date" tick={AXIS_STYLE} minTickGap={24} />
                  <YAxis tick={AXIS_STYLE} allowDecimals={false} width={36} />
                  <Tooltip />
                  <Bar isAnimationActive={false} dataKey="events" fill="#1B33C0" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : null}
            <table className="data-table">
              <thead>
                <tr>
                  <th>Event type</th>
                  <th>Count · 28d</th>
                </tr>
              </thead>
              <tbody>
                {metaEvents.map((e, i) => (
                  <tr key={i}>
                    <td>{str(e["event"]) ?? "—"}</td>
                    <td>{fmtInt(num(e["count"]))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        ) : null}
      </section>

      <section className="panel">
        <h2>Behavior — Microsoft Clarity</h2>
        <SourceState snap={clarity} name="Clarity" />
        {clarityOk ? (
          <>
            <p className="member-note">
              Last {num(clarity.payload["num_days"]) ?? 3} days:{" "}
              <strong>{fmtInt(num(claritySummary["sessions"]))}</strong>{" "}
              sessions ·{" "}
              <strong>{fmtInt(num(claritySummary["distinct_users"]))}</strong>{" "}
              users · <strong>{fmtInt(num(claritySummary["rage_clicks"]))}</strong>{" "}
              rage / <strong>{fmtInt(num(claritySummary["dead_clicks"]))}</strong>{" "}
              dead clicks
              {scrollDepth !== null ? (
                <>
                  {" "}
                  · <strong>{scrollDepth.toFixed(0)}%</strong> avg scroll depth
                </>
              ) : null}
            </p>
            {clarityChart.length > 1 ? (
              <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                <LineChart data={clarityChart} margin={{ top: 8, right: 16 }}>
                  <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                  <XAxis dataKey="date" tick={AXIS_STYLE} minTickGap={24} />
                  <YAxis tick={AXIS_STYLE} allowDecimals={false} width={36} />
                  <Tooltip />
                  <Line
                  isAnimationActive={false}
                    type="monotone"
                    dataKey="sessions"
                    stroke="#1B33C0"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                  isAnimationActive={false}
                    type="monotone"
                    dataKey="rage"
                    stroke="#c0182f"
                    strokeWidth={1.5}
                    dot={false}
                  />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <p className="member-note subtle">
                The trend chart fills in as daily snapshots accumulate
                (Clarity&apos;s API covers only the last 3 days per pull).
              </p>
            )}
            <div className="analytics-tables">
              <div>
                <h3>Pages</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Page</th>
                      <th>Sessions</th>
                      <th>Rage</th>
                      <th>Dead</th>
                      <th>Scroll</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clarityPages.slice(0, 10).map((p, i) => {
                      const depth = num(p["avg_scroll_depth"]);
                      return (
                        <tr key={i}>
                          <td className="analytics-url">
                            {(str(p["url"]) ?? "—").replace(
                              /^https?:\/\/[^/]+/,
                              "",
                            ) || "/"}
                          </td>
                          <td>{fmtInt(num(p["sessions"]))}</td>
                          <td>{fmtInt(num(p["rage_clicks"]))}</td>
                          <td>{fmtInt(num(p["dead_clicks"]))}</td>
                          <td>{depth === null ? "—" : `${depth.toFixed(0)}%`}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Devices</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Device</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clarityDevices.map((d, i) => (
                      <tr key={i}>
                        <td>{str(d["device"]) ?? "—"}</td>
                        <td>{fmtInt(num(d["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div>
                <h3>Sources</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Source</th>
                      <th>Sessions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {claritySources.map((s, i) => (
                      <tr key={i}>
                        <td>{str(s["source"]) ?? "—"}</td>
                        <td>{fmtInt(num(s["sessions"]))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </section>

      <section className="panel">
        {/*
          When the pull failed (or has never run) the account handle is simply
          unknown, and the old `?? "account"` fallback rendered "Instagram —
          @account" — which reads as a real handle rather than an absent one.
          Surfaced by the §8 browser walk, the first to view this panel in its
          error state. Omit the handle instead of inventing one.
        */}
        <h2>
          Instagram
          {igUsername === null || igUsername === "" ? "" : ` — @${igUsername}`}
        </h2>
        {ig === null ? (
          <p className="empty">
            No Instagram snapshot yet — the{" "}
            <code>instagram_insights_daily</code> cron runs at 05:50 and 17:50
            UTC (or invoke it from Inngest / the Status page).
          </p>
        ) : igError !== null ? (
          <p className="member-note">
            <span className="tag tag-danger">{igError}</span>{" "}
            <span className="subtle">
              last pull failed on {ig.captured_on} — check the
              instagram.access_token / instagram.business_account_id secrets
              and re-invoke the cron.
            </span>
          </p>
        ) : (
          <>
            <p className="member-note">
              <strong>{fmtInt(igFollowers)}</strong> followers ·{" "}
              <strong>{fmtInt(igAccount["media_count"] as number | null)}</strong>{" "}
              posts
              {igDayLabel !== null ? (
                <span className="subtle">
                  {" "}
                  · day figures cover the bucket ending {igDayLabel}
                </span>
              ) : null}
            </p>

            <div className="stat-grid">
              <div className="stat-card">
                <div className="label">Reach · 1d</div>
                <div className="value">{fmtInt(num(igDay["reach"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Views · 1d</div>
                <div className="value">{fmtInt(num(igDay["views"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Interactions · 1d</div>
                <div className="value">
                  {fmtInt(num(igDay["total_interactions"]))}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Accounts engaged · 1d</div>
                <div className="value">
                  {fmtInt(num(igDay["accounts_engaged"]))}
                </div>
              </div>
              <div className="stat-card">
                <div className="label">Profile views · 1d</div>
                <div className="value">{fmtInt(num(igDay["profile_views"]))}</div>
              </div>
              <div className="stat-card">
                <div className="label">Link taps · 1d</div>
                <div className="value">
                  {fmtInt(
                    (num(igDay["website_clicks"]) ?? 0) +
                      (num(igDay["profile_links_taps"]) ?? 0),
                  )}
                </div>
              </div>
            </div>

            {igReachBreakdown !== null ? (
              <p className="member-note">
                Reach split:{" "}
                <strong>{fmtInt(num(igReachBreakdown["follower"]))}</strong>{" "}
                followers ·{" "}
                <strong>{fmtInt(num(igReachBreakdown["non_follower"]))}</strong>{" "}
                non-followers
                <span className="subtle">
                  {" "}
                  — the non-follower share is the discovery signal.
                </span>
              </p>
            ) : null}

            {igReachChart.length > 0 ? (
              <>
                <h3>Daily reach · {igReachChart.length}d</h3>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <LineChart data={igReachChart} margin={{ top: 8, right: 16 }}>
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                    <XAxis dataKey="date" tick={AXIS_STYLE} minTickGap={24} />
                    <YAxis tick={AXIS_STYLE} allowDecimals={false} width={44} />
                    <Tooltip />
                    <Line
                      isAnimationActive={false}
                      type="monotone"
                      dataKey="reach"
                      stroke="#c13584"
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </>
            ) : null}

            {igFollowerChart.length > 0 ? (
              <>
                <h3>New followers per day</h3>
                <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
                  <BarChart
                    data={igFollowerChart}
                    margin={{ top: 8, right: 16 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.25} />
                    <XAxis dataKey="date" tick={AXIS_STYLE} minTickGap={24} />
                    <YAxis tick={AXIS_STYLE} allowDecimals={false} width={36} />
                    <Tooltip />
                    <Bar
                      isAnimationActive={false}
                      dataKey="followers"
                      fill="#c13584"
                      radius={[2, 2, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              </>
            ) : null}

            {igTopMedia.length > 0 ? (
              <>
                <h3>Recent posts</h3>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Posted</th>
                      <th>Type</th>
                      <th>Reach</th>
                      <th>Views</th>
                      <th>Likes</th>
                      <th>Comments</th>
                      <th>Saved</th>
                      <th>Shares</th>
                      <th>Follows</th>
                    </tr>
                  </thead>
                  <tbody>
                    {igTopMedia.map((m, i) => {
                      const metrics = rec(m["metrics"]);
                      const permalink = str(m["permalink"]);
                      const ts = str(m["timestamp"]);
                      return (
                        <tr key={i}>
                          <td>
                            {permalink !== null ? (
                              <a
                                href={permalink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {ts === null ? "—" : shortDate(ts.slice(0, 10))}
                              </a>
                            ) : ts === null ? (
                              "—"
                            ) : (
                              shortDate(ts.slice(0, 10))
                            )}
                          </td>
                          <td>
                            {str(m["media_product_type"]) ??
                              str(m["media_type"]) ??
                              "—"}
                          </td>
                          <td>{fmtInt(num(metrics["reach"]))}</td>
                          <td>{fmtInt(num(metrics["views"]))}</td>
                          {/* Inline list counts are the fallback: they survive
                              even when a post's own insights call failed. */}
                          <td>
                            {fmtInt(
                              num(metrics["likes"]) ??
                                num(m["like_count"]),
                            )}
                          </td>
                          <td>
                            {fmtInt(
                              num(metrics["comments"]) ??
                                num(m["comments_count"]),
                            )}
                          </td>
                          <td>{fmtInt(num(metrics["saved"]))}</td>
                          <td>{fmtInt(num(metrics["shares"]))}</td>
                          <td>{fmtInt(num(metrics["follows"]))}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : null}

          </>
        )}

        {/* 2026-08-06 (migration 156) — stories. Deliberately OUTSIDE the
            ig === null / igError ternary above: it reads
            `instagram_story_insights`, not `ig.payload`, so a failed account
            pull (or a day with no snapshot at all) must not hide stories that
            were already recorded. They are separate data with a separate
            failure mode. */}
        {igStories.length > 0 ? (
              <>
                <h3>Stories · last {igStories.length}</h3>
                <p className="muted">
                  Stories expire after 24h, so these are pulled every 12h and
                  each row keeps its most complete reading. “Seen” is how many
                  runs caught the story alive — a story seen once means the
                  poll only just managed it.
                </p>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Posted</th>
                      <th>Reach</th>
                      <th>Views</th>
                      <th>Replies</th>
                      <th>Shares</th>
                      <th>Navigation</th>
                      <th>Follows</th>
                      <th>Profile visits</th>
                      <th>Seen</th>
                    </tr>
                  </thead>
                  <tbody>
                    {igStories.map((s) => {
                      const m = s.metrics;
                      const posted =
                        s.posted_at === null
                          ? null
                          : shortDate(s.posted_at.slice(0, 10));
                      return (
                        <tr key={s.story_id}>
                          <td>
                            {s.permalink !== null ? (
                              <a
                                href={s.permalink}
                                target="_blank"
                                rel="noreferrer"
                              >
                                {posted ?? "—"}
                              </a>
                            ) : (
                              posted ?? "—"
                            )}
                          </td>
                          {s.metrics_error !== null ? (
                            <td colSpan={7} className="muted">
                              metrics unavailable ({s.metrics_error})
                            </td>
                          ) : (
                            <>
                              <td>{fmtInt(num(m["reach"]))}</td>
                              <td>{fmtInt(num(m["views"]))}</td>
                              <td>{fmtInt(num(m["replies"]))}</td>
                              <td>{fmtInt(num(m["shares"]))}</td>
                              <td>{fmtInt(num(m["navigation"]))}</td>
                              <td>{fmtInt(num(m["follows"]))}</td>
                              <td>{fmtInt(num(m["profile_visits"]))}</td>
                            </>
                          )}
                          <td>{s.observations}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </>
            ) : null}
      </section>
    </div>
  );
}
